import { DOCUMENT } from '@angular/common'
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'
import { DomSanitizer } from '@angular/platform-browser'
import { DownloadService } from '../../services/download.service'
import {
  alsPdfVersuchen,
  begrenzeProzent,
  type Flaeche,
  gedreht,
  naechsteStufe,
  pdfDateiname,
  ragtUeber,
  type PdfZoom,
  prozentVon,
  radZoomFaktor,
  RAND,
  seiteAnPosition,
  seitenverhaeltnis,
  skalaFuer,
  zoomBeschriftung,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_STUFEN,
} from './pdf-ansicht'
import type { PdfDokument } from './pdf-dokument'
import { PdfSeiteComponent } from './pdf-seite.component'

const A4_HOCHKANT = '210 / 297'

type Modus = 'leer' | 'laedt' | 'eigen' | 'browser'

/** Safaris Pinch-Geste auf dem Trackpad; die anderen Browser melden sie als Mausrad mit Strg. */
interface GestenEreignis extends UIEvent {
  readonly scale: number
  readonly clientX: number
  readonly clientY: number
}

/**
 * Zeigt ein PDF, und zwar selbst gezeichnet (pdf.js) statt im Viewer des Browsers. Damit sieht es in
 * Chrome, Edge, Firefox und Safari gleich aus: Start auf der ganzen Seite (solange sie lesbar
 * bleibt, `skalaFuer`), keine Seitenleiste, Zoom,
 * Drehen, Herunterladen und „In neuem Tab öffnen" in einer Leiste ueber dem Blatt. Der Text bleibt
 * markierbar, Strg+F findet ihn, und Links lassen sich anklicken (auch eine Adresse, die nur als Text
 * dasteht, `pdf-seite.component.ts`).
 *
 * Bis 2026-10 stand hier ein `<object>`, und jeder Browser zeigte seine eigene Oberflaeche: Chrome
 * merkte sich eine aufgeklappte Seitenleiste je Nutzer:in (auch bei einem Rezept mit einer Seite, das
 * dann noch rund 250 px breit war), und Strg+Mausrad zoomte im Viewer nur das PDF, sodass sich die
 * Seite danach nicht mehr zoomen liess. Klappt das Zeichnen nicht (pdf.js laedt nicht, die Datei ist
 * verschluesselt oder kaputt), steht wie frueher der Viewer des Browsers da.
 *
 * pdf.js kommt per dynamischem Import nach (`pdf-engine.ts`): diese Komponente liegt im
 * `components`-Barrel und damit im Start-Bundle aller drei Apps.
 */
@Component({
  selector: 'lib-pdf',
  imports: [MatButtonModule, MatIconModule, MatMenuModule, MatProgressSpinnerModule, MatTooltipModule, PdfSeiteComponent],
  templateUrl: './pdf.component.html',
  styleUrl: './pdf.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfComponent {
  private readonly sanitizer = inject(DomSanitizer)
  private readonly downloadService = inject(DownloadService)
  private readonly document = inject(DOCUMENT)
  private readonly injector = inject(Injector)

  readonly pdf = input<Blob | string | null | undefined>(undefined)

  /**
   * Hoehe aus der ersten Seite des PDFs statt fest A4 hochkant. Ein quer fotografiertes Rezept
   * steht damit quer da und nicht als schmaler Streifen in einer Hochkant-Flaeche.
   */
  readonly seitenformatAusDatei = input(false)

  /**
   * Fuellt die Hoehe des Elternelements, statt sich am Seitenformat zu bemessen. Fuer einen Kasten
   * mit fester Hoehe (Dialog, Vorschau im Formular); `lib-pdf` braucht dann selbst eine Hoehe.
   */
  readonly fuellen = input(false)

  /** Dateiname beim Herunterladen, ohne oder mit `.pdf`. Der PDF-Dialog gibt seinen Titel. */
  readonly dateiname = input<string>()

  protected readonly zoomStufen = ZOOM_STUFEN
  protected readonly zoomMin = ZOOM_MIN
  protected readonly zoomMax = ZOOM_MAX

  protected readonly modus = signal<Modus>('leer')
  protected readonly dokument = signal<PdfDokument | undefined>(undefined)
  protected readonly drehung = signal(0)
  protected readonly zoom = signal<PdfZoom>({ art: 'automatisch' })
  protected readonly aktuelleSeite = signal(1)
  /** Adresse fuer „In neuem Tab öffnen" und fuer den Rueckfall auf den Browser. */
  protected readonly url = signal<string | undefined>(undefined)

  private readonly flaechenMass = signal<Flaeche>({ breite: 0, hoehe: 0 })
  private readonly flaeche = viewChild<ElementRef<HTMLElement>>('flaeche')

  protected readonly sichereUrl = computed(() => {
    const url = this.url()
    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : undefined
  })
  protected readonly skala = computed(() => skalaFuer(this.zoom(), this.dokument()?.seiten ?? [], this.drehung(), this.flaechenMass()))
  protected readonly prozent = computed(() => prozentVon(this.skala()))
  protected readonly zoomText = computed(() => zoomBeschriftung(this.zoom(), this.prozent()))
  protected readonly seitenAnzahl = computed(() => this.dokument()?.seiten.length ?? 0)
  protected readonly seiten = computed(() => {
    const skala = this.skala()
    const drehung = this.drehung()
    return (this.dokument()?.seiten ?? []).map((seite, index) => {
      const g = gedreht(seite, drehung)
      return { nummer: index + 1, breite: g.breite * skala, hoehe: g.hoehe * skala }
    })
  })
  protected readonly verschiebbar = computed(() => ragtUeber(this.seiten(), this.flaechenMass()))
  protected readonly zieht = signal(false)
  protected readonly seitenformat = computed(() => {
    const erste = this.dokument()?.seiten[0]
    return this.seitenformatAusDatei() && erste ? seitenverhaeltnis(erste, this.drehung()) : A4_HOCHKANT
  })

  private lauf = 0
  private eigeneUrl?: string
  private scrollAngefragt = false

  constructor() {
    effect(() => {
      const pdf = this.pdf()
      untracked(() => void this.lade(pdf))
    })

    effect((onCleanup) => {
      const element = this.flaeche()?.nativeElement
      if (!element) {
        return
      }
      const messen = () => this.flaechenMass.set({ breite: element.clientWidth, hoehe: element.clientHeight })
      const groesse = new ResizeObserver(messen)
      groesse.observe(element)
      messen()

      const rad = (ereignis: WheelEvent) => this.radZoom(ereignis, element)
      let gestenStart = 100
      const gesteBeginnt = (ereignis: Event) => {
        ereignis.preventDefault()
        gestenStart = this.prozent()
      }
      const gesteLaeuft = (ereignis: Event) => {
        ereignis.preventDefault()
        const geste = ereignis as GestenEreignis
        this.zoomeUm(begrenzeProzent(gestenStart * geste.scale), element, geste.clientX, geste.clientY)
      }
      const ziehen = (ereignis: PointerEvent) => this.zieheStart(ereignis, element)
      element.addEventListener('wheel', rad, { passive: false })
      element.addEventListener('gesturestart', gesteBeginnt)
      element.addEventListener('gesturechange', gesteLaeuft)
      element.addEventListener('pointerdown', ziehen)
      onCleanup(() => {
        groesse.disconnect()
        element.removeEventListener('pointerdown', ziehen)
        element.removeEventListener('wheel', rad)
        element.removeEventListener('gesturestart', gesteBeginnt)
        element.removeEventListener('gesturechange', gesteLaeuft)
      })
    })

    inject(DestroyRef).onDestroy(() => {
      this.lauf++
      this.aufraeumen()
    })
  }

  protected vergroessern() {
    this.zoomeUmMitte(naechsteStufe(this.prozent(), 1))
  }

  protected verkleinern() {
    this.zoomeUmMitte(naechsteStufe(this.prozent(), -1))
  }

  protected setzeProzent(prozent: number) {
    this.zoomeUmMitte(prozent)
  }

  protected passeEin(art: 'seitenbreite' | 'ganzeSeite') {
    this.zoom.set({ art })
  }

  protected drehen() {
    this.drehung.update((d) => (d + 90) % 360)
  }

  protected herunterladen() {
    const pdf = this.pdf()
    const name = pdfDateiname(this.dateiname())
    if (pdf instanceof Blob) {
      this.downloadService.downloadFile(name, pdf)
    } else if (pdf) {
      const link = this.document.createElement('a')
      link.href = pdf
      link.download = name
      link.rel = 'noopener'
      this.document.body.appendChild(link)
      link.click()
      this.document.body.removeChild(link)
    }
  }

  /** Ein Link im Dokument auf eine andere Seite (Inhaltsverzeichnis, Querverweis). */
  protected zeigeSeite(nummer: number) {
    const element = this.flaeche()?.nativeElement
    const seite = element?.querySelectorAll<HTMLElement>('lib-pdf-seite')[nummer - 1]
    if (element && seite) {
      element.scrollTo({ top: seite.offsetTop - RAND, behavior: 'smooth' })
    }
  }

  protected beimScrollen() {
    if (this.scrollAngefragt) {
      return
    }
    this.scrollAngefragt = true
    requestAnimationFrame(() => {
      this.scrollAngefragt = false
      const element = this.flaeche()?.nativeElement
      if (!element) {
        return
      }
      const oberkanten = Array.from(element.querySelectorAll<HTMLElement>('lib-pdf-seite'), (seite) => seite.offsetTop)
      this.aktuelleSeite.set(Math.min(this.seitenAnzahl(), seiteAnPosition(oberkanten, element.scrollTop, element.clientHeight)))
    })
  }

  /** Strg+Mausrad (und die Pinch-Geste auf dem Trackpad in Chrome, Edge, Firefox) zoomt das PDF, nicht die Seite. */
  private radZoom(ereignis: WheelEvent, element: HTMLElement) {
    if (!ereignis.ctrlKey) {
      return
    }
    ereignis.preventDefault()
    const neu = begrenzeProzent(this.prozent() * radZoomFaktor(ereignis.deltaY, ereignis.deltaMode))
    this.zoomeUm(neu, element, ereignis.clientX, ereignis.clientY)
  }

  /**
   * Mit gedrueckter Maustaste das Blatt verschieben, sobald es ueber die Flaeche hinausragt. Vorher ging
   * das nur ueber die Scrollbalken (Befund Tim 2026-10-25 am gezoomten Rezept). Auf Text bleibt Ziehen
   * Markieren, und Touch scrollt ohnehin selbst. Auf einem Link startet es nicht: mit dem Pointer-Capture
   * ginge der Klick sonst an die Flaeche statt an den Link.
   */
  private zieheStart(ereignis: PointerEvent, element: HTMLElement) {
    if (ereignis.pointerType !== 'mouse' || ereignis.button !== 0 || !this.verschiebbar()) {
      return
    }
    if ((ereignis.target as Element).closest('.textLayer span, .textLayer br, .lib-pdf-link')) {
      return
    }
    // Ein Klick in den Scrollbalken gehoert dem Scrollbalken (Chrome meldet ihn als pointerdown der Flaeche).
    const rahmen = element.getBoundingClientRect()
    if (
      ereignis.clientX - rahmen.left >= element.clientLeft + element.clientWidth ||
      ereignis.clientY - rahmen.top >= element.clientTop + element.clientHeight
    ) {
      return
    }
    ereignis.preventDefault()
    element.focus({ preventScroll: true })
    const start = { x: ereignis.clientX, y: ereignis.clientY, links: element.scrollLeft, oben: element.scrollTop }
    const bewegen = (e: PointerEvent) => {
      element.scrollLeft = start.links - (e.clientX - start.x)
      element.scrollTop = start.oben - (e.clientY - start.y)
    }
    const ende = () => {
      element.removeEventListener('pointermove', bewegen)
      element.removeEventListener('pointerup', ende)
      element.removeEventListener('pointercancel', ende)
      this.zieht.set(false)
    }
    element.setPointerCapture(ereignis.pointerId)
    element.addEventListener('pointermove', bewegen)
    element.addEventListener('pointerup', ende)
    element.addEventListener('pointercancel', ende)
    this.zieht.set(true)
  }

  private zoomeUmMitte(prozent: number) {
    const element = this.flaeche()?.nativeElement
    if (!element) {
      this.zoom.set({ art: 'prozent', wert: begrenzeProzent(prozent) })
      return
    }
    const rahmen = element.getBoundingClientRect()
    this.zoomeUm(begrenzeProzent(prozent), element, rahmen.left + rahmen.width / 2, rahmen.top + rahmen.height / 2)
  }

  /** Zoomt so, dass der Punkt unter dem Zeiger (bzw. die Mitte) stehen bleibt. */
  private zoomeUm(prozent: number, element: HTMLElement, clientX: number, clientY: number) {
    const vorher = this.prozent()
    if (Math.abs(prozent - vorher) < 0.01) {
      return
    }
    const rahmen = element.getBoundingClientRect()
    const x = clientX - rahmen.left
    const y = clientY - rahmen.top
    const verhaeltnis = prozent / vorher
    const links = (element.scrollLeft + x) * verhaeltnis - x
    const oben = (element.scrollTop + y) * verhaeltnis - y
    this.zoom.set({ art: 'prozent', wert: prozent })
    afterNextRender(
      () => {
        element.scrollLeft = links
        element.scrollTop = oben
      },
      { injector: this.injector },
    )
  }

  private async lade(pdf: Blob | string | null | undefined) {
    const lauf = ++this.lauf
    this.aufraeumen()
    this.drehung.set(0)
    this.zoom.set({ art: 'automatisch' })
    this.aktuelleSeite.set(1)

    if (!pdf || (pdf instanceof Blob && !alsPdfVersuchen(pdf))) {
      this.modus.set('leer')
      return
    }
    if (typeof pdf === 'string') {
      this.url.set(pdf)
    } else {
      // Ohne Typ zeigte der neue Tab Zeichensalat statt des PDF-Viewers.
      const alsPdf = pdf.type === 'application/pdf' ? pdf : new Blob([pdf], { type: 'application/pdf' })
      this.eigeneUrl = URL.createObjectURL(alsPdf)
      this.url.set(this.eigeneUrl)
    }
    this.modus.set('laedt')

    try {
      const engine = await import('./pdf-engine')
      const quelle = typeof pdf === 'string' ? pdf : new Uint8Array(await pdf.arrayBuffer())
      const dokument = await engine.oeffnePdf(quelle)
      if (lauf !== this.lauf) {
        dokument.zerstoere()
        return
      }
      this.dokument.set(dokument)
      this.modus.set('eigen')
    } catch {
      if (lauf === this.lauf) {
        this.modus.set('browser')
      }
    }
  }

  private aufraeumen() {
    this.dokument()?.zerstoere()
    this.dokument.set(undefined)
    this.url.set(undefined)
    const url = this.eigeneUrl
    this.eigeneUrl = undefined
    if (url) {
      // Erst spaeter freigeben: ein gerade geoeffneter Tab liest die Adresse womoeglich noch (wie im DownloadService).
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    }
  }
}
